import { useLazyQuery, useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useState, useEffect, useCallback } from "react";
import {
  ImageAttachedToKind,
  ImageSourcePlatform2,
  ImageUploadStatus2,
} from "types/globalTypes";

const CREATE_IMAGE_UPLOAD_MUTATION = gql`
  mutation createImageUpload(
    $attachedToId: ID!
    $attachedToKind: ImageAttachedToKind!
    $sourcePlatform: ImageSourcePlatform2!
  ) {
    createImageUpload3(
      input: {
        attached_to_id: $attachedToId
        attached_to_kind: $attachedToKind
        sourcePlatform: $sourcePlatform
      }
    ) {
      id
      s3PresignedPost {
        fields
        url
      }
    }
  }
`;

const IMAGE_UPLOAD_STATUS_QUERY = gql`
  query IMAGE_UPLOAD_STATUS_QUERY($imageUploadIds: [ID!]!) {
    imageUploads2(imageUploadIds: $imageUploadIds) {
      id
      status
      attachedTo {
        id
        kind
      }
    }
  }
`;

interface Args {
  attachedToId: string;
  attachedToKind: ImageAttachedToKind;
  sourcePlatform?: ImageSourcePlatform2;
  imageFiles: FileList;
}

interface IContext {
  called: boolean;
}

const useUploadImage = (): [Function, IContext] => {
  const [createImageUpload] = useMutation(CREATE_IMAGE_UPLOAD_MUTATION);
  const [context, setContext] = useState<IContext>({
    called: false,
  });

  const execute = useCallback(
    ({
      attachedToId,
      attachedToKind,
      sourcePlatform = ImageSourcePlatform2.WEB,
      imageFiles,
    }: Args) => {
      const uploadRequests = Array.from(imageFiles).map(async (imageFile) => {
        const {
          data: {
            createImageUpload3: {
              id: uploadId,
              s3PresignedPost: { fields, url },
            },
          },
        } = await createImageUpload({
          variables: { attachedToId, attachedToKind, sourcePlatform },
        });

        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          if (typeof value === "string") formData.append(key, value);
        });
        formData.append("file", imageFile);

        // with mode: no-cors the reponse is opaque which means we cannot process it. This optimsistically assumes that the POST worked.
        // if it didn't then the status poll will always sit at AWAITING_UPLOAD
        await fetch(url, {
          method: "POST",
          body: formData,
          mode: "no-cors",
        });
        return uploadId;
      });
      setContext({ ...context, called: true });
      return Promise.all(uploadRequests);
    },
    [createImageUpload, setContext, context]
  );

  return [execute, context];
};

const usePollImages = (): [
  Function,
  { id: string; status: ImageUploadStatus2 }[]
] => {
  const [queryImageStatuses, { data, loading, stopPolling }] = useLazyQuery(
    IMAGE_UPLOAD_STATUS_QUERY
  );
  const [imageUploadIds, setImageUploadIds] = useState<string[]>([]);
  const [completedUploads, setCompletedUploads] = useState<
    { id: string; status: ImageUploadStatus2 }[]
  >([]);

  useEffect(() => {
    if (imageUploadIds.length > 0) {
      queryImageStatuses({
        variables: { imageUploadIds },
        pollInterval: 2000,
      });
    } else {
      stopPolling();
    }
  }, [queryImageStatuses, imageUploadIds, stopPolling]);

  useEffect(() => {
    if (data && !loading) {
      const { imageUploads2 } = data;
      const stillInProgressIds: string[] = [];
      const finishedUploads: { id: string; status: ImageUploadStatus2 }[] = [];
      imageUploads2.forEach(
        ({ id, status }: { id: string; status: ImageUploadStatus2 }) => {
          if (
            [
              ImageUploadStatus2.PUBLISHED,
              ImageUploadStatus2.DUPLICATED,
              ImageUploadStatus2.FAILED,
            ].includes(status)
          )
            finishedUploads.push({ id, status });
          if (
            ![
              ImageUploadStatus2.PUBLISHED,
              ImageUploadStatus2.DUPLICATED,
              ImageUploadStatus2.FAILED,
            ].includes(status)
          )
            stillInProgressIds.push(id);
        }
      );
      setImageUploadIds(stillInProgressIds);
      setCompletedUploads([...completedUploads, ...finishedUploads]);
    }
  }, [data, loading]);

  const execute = useCallback(
    (ids: string[]) => {
      // imageUploadIds is always [] when this is called.
      // It could be an issue if more images are uploaded after an inital batch.
      setImageUploadIds([...imageUploadIds, ...ids]);
      setCompletedUploads([]);
    },
    [imageUploadIds]
  );
  return [execute, completedUploads];
};

export { useUploadImage, usePollImages, CREATE_IMAGE_UPLOAD_MUTATION };
